import type { SpeedMode } from "./SpeedMode";

/**
 * How many times faster than normal each accelerated speed mode plays the presentation. Each is finite and
 * above 0 (below 1 plays slower than normal); `PresentationClock` throws on anything else when it is built,
 * so a bad value fails the boot instead of reaching the runner and Spine (review 2026-09-27 #62).
 */
export type SpeedModeScales = Readonly<Record<Exclude<SpeedMode, "normal">, number>>;

/** The platform's speeds, for a game that sets none. */
const DEFAULT_SPEED_MODE_SCALES: SpeedModeScales = { turbo: 2.25, super_turbo: 4 };

/**
 * The speed mode and what it does to every runner duration. A game sets its turbo speeds through its
 * definition; until 2026-09-26 they were fixed here under a comment telling the reader to tune them,
 * which a title could not do (review finding 47).
 */
export class PresentationClock {
  private mode: SpeedMode = "normal";
  private readonly scaleByMode: Readonly<Record<SpeedMode, number>>;

  public constructor(scales: SpeedModeScales = DEFAULT_SPEED_MODE_SCALES) {
    for (const [mode, scale] of Object.entries(scales)) {
      if (!Number.isFinite(scale) || scale <= 0) throw new Error(`PresentationClock: the ${mode} scale must be finite and above 0, got ${scale}`);
    }
    this.scaleByMode = { normal: 1, ...scales };
  }

  public setMode(mode: SpeedMode): void {
    this.mode = mode;
  }

  public getMode(): SpeedMode {
    return this.mode;
  }

  /** Higher scale => faster playback => shorter durations */
  public getTimeScale(): number {
    return this.scaleByMode[this.mode] ?? 1;
  }

  /** A duration as the runner plays it in the current mode. */
  public scaleDurationSeconds(seconds: number): number {
    return seconds / this.getTimeScale();
  }
}
